/**
 * SlideCanvas — single slide renderer used by both the internal SalesDeckPage
 * and the public/shareable PublicSalesDeckPage. Footer pulls from the supplied
 * `brand` object so each whitelabel tenant sees their own company name + domain.
 */
import {
  Sparkles, Inbox, FileText, DollarSign, Users, Calendar, Star, Gift,
  MessageCircle, Package, BarChart3, Zap, Send, RefreshCw, Trophy, Repeat,
  Lock, Award, BellRing, Clock, FolderOpen, Share2, ShoppingCart,
  GraduationCap, Smartphone, Heart, TrendingDown, Target, ArrowRight,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useState } from 'react';

const ICONS = {
  Sparkles, Inbox, FileText, DollarSign, Users, Calendar, Star, Gift,
  MessageCircle, Package, BarChart3, Zap, Send, RefreshCw, Trophy, Repeat,
  Lock, Award, BellRing, Clock, FolderOpen, Share2, ShoppingCart,
  GraduationCap, Smartphone, Heart, TrendingDown, Target,
};

export const SlideCanvas = ({ slide, deckLabel, brand, leadCapture }) => {
  const brandName = brand?.name || 'Konnect';
  const brandDomain = brand?.domain || 'konnect.kaharian.com';
  const accent = brand?.accent || '#10b981'; // emerald-500 default
  return (
    <div
      className="bg-gradient-to-br from-emerald-50 via-white to-teal-50 border rounded-xl shadow-md min-h-[520px] p-8 sm:p-12 flex flex-col"
      data-testid={`sales-deck-slide-${slide.kind}`}
    >
      {(() => {
        switch (slide.kind) {
          case 'cover': return <CoverSlide slide={slide} accent={accent} />;
          case 'pain_points': return <PainSlide slide={slide} />;
          case 'solution': return <SolutionSlide slide={slide} accent={accent} />;
          case 'before_after': return <BeforeAfterSlide slide={slide} />;
          case 'metrics': return <MetricsSlide slide={slide} />;
          case 'pricing': return <PricingSlide slide={slide} />;
          case 'cta': return <CTASlide slide={slide} accent={accent} leadCapture={leadCapture} brandName={brandName} />;
          default: return <pre className="text-xs">{JSON.stringify(slide, null, 2)}</pre>;
        }
      })()}
      <div className="mt-auto pt-6 text-xs text-gray-400 flex items-center justify-between border-t border-gray-100">
        <span>{brandName} — for {deckLabel}</span>
        <span>{brandDomain}</span>
      </div>
    </div>
  );
};

const CoverSlide = ({ slide, accent }) => (
  <div className="text-center max-w-3xl mx-auto">
    <div className="text-xs uppercase tracking-widest mb-3" style={{ color: accent }}>
      Built for {slide.audience}
    </div>
    <h2 className="text-4xl sm:text-5xl font-heading font-bold text-text-primary leading-tight mb-4">
      {slide.title}
    </h2>
    <p className="text-lg text-gray-medium mb-8">{slide.subtitle}</p>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
      {slide.bullets.map((b, i) => (
        <div key={i} className="bg-white rounded-lg border border-emerald-100 px-3 py-3 text-sm text-emerald-900 font-medium">
          {b}
        </div>
      ))}
    </div>
  </div>
);

const PainSlide = ({ slide }) => (
  <div className="max-w-4xl mx-auto w-full">
    <h2 className="text-3xl font-heading font-bold text-text-primary mb-2">{slide.title}</h2>
    <p className="text-base text-gray-medium mb-6">{slide.subtitle}</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {slide.items.map((p, i) => (
        <div key={i} className="bg-white border border-gray-200 rounded-lg p-5 flex gap-4">
          <div className="text-3xl shrink-0">{p.emoji}</div>
          <div>
            <div className="font-semibold text-text-primary mb-1">{p.title}</div>
            <div className="text-sm text-gray-600 leading-relaxed">{p.body}</div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SolutionSlide = ({ slide, accent }) => {
  const Icon = ICONS[slide.icon] || Sparkles;
  return (
    <div className="max-w-4xl mx-auto w-full">
      <div className="flex items-center gap-3 mb-2">
        <div className="bg-emerald-100 rounded-full p-3"><Icon className="h-6 w-6" style={{ color: accent }} /></div>
        <span className="text-xs uppercase tracking-widest font-semibold" style={{ color: accent }}>{slide.module}</span>
      </div>
      <h2 className="text-3xl font-heading font-bold text-text-primary mb-2">{slide.title}</h2>
      <p className="text-base text-gray-medium mb-6">{slide.subtitle}</p>
      <ul className="space-y-2 mb-6">
        {slide.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-text-primary">
            <span className="mt-0.5" style={{ color: accent }}>✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="text-white rounded-lg p-4 flex items-center gap-3" style={{ backgroundColor: accent }}>
        <ArrowRight className="h-5 w-5 shrink-0" />
        <span className="font-semibold">{slide.outcome}</span>
      </div>
    </div>
  );
};

const BeforeAfterSlide = ({ slide }) => (
  <div className="max-w-5xl mx-auto w-full">
    <h2 className="text-3xl font-heading font-bold text-text-primary mb-6 text-center">{slide.title}</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div className="bg-red-50 border border-red-200 rounded-lg p-5">
        <div className="text-xs uppercase tracking-widest text-red-600 font-semibold mb-3">{slide.before_label}</div>
        <ul className="space-y-3">
          {slide.rows.map((r, i) => (
            <li key={i} className="text-sm text-red-900">• {r.before}</li>
          ))}
        </ul>
      </div>
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5">
        <div className="text-xs uppercase tracking-widest text-emerald-600 font-semibold mb-3">{slide.after_label}</div>
        <ul className="space-y-3">
          {slide.rows.map((r, i) => (
            <li key={i} className="text-sm text-emerald-900">✓ {r.after}</li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const MetricsSlide = ({ slide }) => (
  <div className="max-w-4xl mx-auto w-full">
    <h2 className="text-3xl font-heading font-bold text-text-primary mb-2 text-center">{slide.title}</h2>
    <p className="text-base text-gray-medium mb-8 text-center">{slide.subtitle}</p>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
      {slide.metrics.map((m, i) => (
        <div key={i} className="bg-white border border-emerald-200 rounded-lg p-5 text-center">
          <div className={`text-3xl font-bold ${m.trend === 'down' ? 'text-emerald-700' : 'text-emerald-600'}`}>
            {m.value}
          </div>
          <div className="text-xs text-gray-medium mt-2">{m.label}</div>
        </div>
      ))}
    </div>
  </div>
);

const PricingSlide = ({ slide }) => (
  <div className="max-w-2xl mx-auto w-full text-center">
    <div className="inline-block bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-widest rounded-full px-3 py-1 mb-3">
      Recommended for you
    </div>
    <h2 className="text-3xl font-heading font-bold text-text-primary mb-2">{slide.title}</h2>
    <p className="text-base text-gray-medium mb-6">{slide.subtitle}</p>
    <div className="bg-white border-2 border-emerald-500 rounded-xl p-6 shadow-lg">
      <div className="flex items-baseline justify-center gap-1 mb-4">
        <span className="text-5xl font-heading font-bold text-text-primary">{slide.price}</span>
        <span className="text-base text-gray-medium">{slide.period}</span>
      </div>
      <ul className="space-y-2 text-left mb-4">
        {slide.features.map((f, i) => (
          <li key={i} className="flex items-start gap-2 text-sm text-text-primary">
            <span className="text-emerald-500 mt-0.5">✓</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="text-xs text-emerald-700 font-semibold border-t border-emerald-100 pt-3">
        {slide.guarantee}
      </div>
    </div>
  </div>
);

const CTASlide = ({ slide, accent, leadCapture, brandName }) => (
  <div className="text-center max-w-3xl mx-auto flex-1 flex flex-col justify-center">
    <h2 className="text-4xl sm:text-5xl font-heading font-bold text-text-primary mb-4">{slide.title}</h2>
    <p className="text-lg text-gray-medium mb-8">{slide.subtitle}</p>
    {leadCapture?.bookingUrl ? (
      <div className="max-w-xl mx-auto w-full">
        <Button
          asChild
          size="lg"
          className="text-white text-base px-8 py-6 h-auto"
          style={{ backgroundColor: accent }}
          data-testid="public-deck-booking-cta"
        >
          <a href={leadCapture.bookingUrl} target="_blank" rel="noopener noreferrer">
            Book directly on my calendar →
          </a>
        </Button>
        <p className="mt-3 text-xs text-gray-400">
          Pick a time that works for you — confirmation in your inbox right away.
        </p>
      </div>
    ) : leadCapture?.onSubmit ? (
      <LeadCaptureForm accent={accent} onSubmit={leadCapture.onSubmit} brandName={brandName} ctaLabel={slide.primary_cta} />
    ) : (
      // Internal preview (no leadCapture wired). Show working buttons that
      // open the same destinations the operator will configure (My Whitelabel
      // → Demo booking URL for primary; signup for secondary). This way the
      // operator can click-through during a live walkthrough.
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button
          asChild
          size="lg"
          className="text-white"
          style={{ backgroundColor: accent }}
          data-testid="internal-cta-book-demo"
        >
          <a href="/my-whitelabel" rel="noopener noreferrer">
            {slide.primary_cta || 'Book Demo'}
          </a>
        </Button>
        <Button
          asChild
          size="lg"
          variant="outline"
          data-testid="internal-cta-signup"
        >
          <a href="/signup" rel="noopener noreferrer">
            {slide.secondary_cta || 'Start Free Trial'}
          </a>
        </Button>
      </div>
    )}
  </div>
);

const LeadCaptureForm = ({ accent, onSubmit, ctaLabel, brandName }) => {
  const [first_name, setFirst] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!first_name || !email) return;
    setSubmitting(true);
    const ok = await onSubmit({ first_name, email, phone, company, message });
    setSubmitting(false);
    if (ok) setDone(true);
  };

  if (done) {
    return (
      <div className="bg-white border-2 border-emerald-500 rounded-xl p-8 max-w-xl mx-auto">
        <div className="text-5xl mb-3">✓</div>
        <h3 className="text-2xl font-heading font-bold mb-2">Thanks, {first_name}!</h3>
        <p className="text-gray-medium">
          Someone from {brandName || 'our team'} will reach out within 1 business day to schedule
          your demo. Watch your inbox — and check spam just in case.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border-2 rounded-xl p-6 sm:p-8 max-w-xl mx-auto text-left space-y-3 shadow-lg" style={{ borderColor: accent }} data-testid="public-deck-lead-form">
      <h3 className="font-heading font-semibold text-lg text-center" style={{ color: accent }}>
        Get a free walkthrough →
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input required placeholder="Your name *" value={first_name} onChange={(e) => setFirst(e.target.value)} data-testid="lead-name" />
        <Input required type="email" placeholder="Work email *" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="lead-email" />
        <Input placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="lead-phone" />
        <Input placeholder="Company / Business name" value={company} onChange={(e) => setCompany(e.target.value)} data-testid="lead-company" />
      </div>
      <Textarea
        rows={2}
        placeholder="Anything specific you'd like us to cover in the demo?"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        data-testid="lead-message"
      />
      <Button
        type="submit"
        size="lg"
        className="w-full text-white"
        style={{ backgroundColor: accent }}
        disabled={submitting || !first_name || !email}
        data-testid="lead-submit"
      >
        {submitting ? 'Sending…' : (ctaLabel || 'Book my free walkthrough')}
      </Button>
      <p className="text-xs text-gray-400 text-center">
        We&apos;ll only use your details to reach out about this demo.
      </p>
    </form>
  );
};
